/**
 * Geocodificador especializado para infraestructuras energéticas de Andalucía
 * 
 * Conecta con servicios WFS de la Agencia Andaluza de la Energía (AAE) para acceder a:
 * - Subestaciones eléctricas: ~200 en Andalucía (MIEA:subestaciones)
 * - Parques eólicos: ~180 (MIEA:cEolicas)
 * - Plantas fotovoltaicas: ~300 (MIEA:csolares)
 * - Centrales térmicas: (MIEA:ctermicas)
 * - Plantas de biomasa: (MIEA:cBiomasa)
 * - Hidroeléctricas: (MIEA:cHidroelectricas)
 * - Cogeneración: (MIEA:cCogeneracion)
 * - Infraestructura gasista: gasoductos, estaciones compresión (MIEA:cGasoductos, MIEA:cCompresion)
 * - Líneas eléctricas: (MIEA:LineasElect)
 * 
 * NOTA: El servicio WFS de AAE NO incluye campos MUNICIPIO ni PROVINCIA,
 * por lo que la búsqueda por municipio requiere filtro espacial BBOX.
 * 
 * Fuentes oficiales:
 * - WFS Agencia Andaluza Energía: https://www.agenciaandaluzadelaenergia.es/mapwms/wfs
 * - Namespace: MIEA (http://axelia:8081/mapwms/miea)
 * - Cumplimiento: INSPIRE Annex III Energy Resources
 * - Proyección nativa: EPSG:25830 (UTM30 ETRS89)
 * 
 * @module services/geocoding/specialized
 * @version 2.0.0 - F2.T4: Capas WFS reales + integración BBOX
 */

import type { 
  WFSFeature, 
  WFSSearchOptions 
} from './WFSBaseGeocoder';
import type { GeoJSONFeature, WFSResponse, GeoJSONFeatureCollection } from '../../../types/geojson';
import { getStringProp } from '../../../types/geojson';
import { 
  WFSBaseGeocoder 
} from './WFSBaseGeocoder';
import { 
  getBBoxByINE, 
  getBBoxByName, 
  validatePointInMunicipality
} from '../../boundaries';
import type { SpecializedGeocoderConfig, GeocodingResult } from '../../../types/infrastructure';

/**
 * Tipos de infraestructuras energéticas
 * Mapeo a capas reales del WFS MIEA
 */
export enum EnergyFacilityType {
  /** Subestaciones eléctricas - MIEA:subestaciones */
  SUBSTATION = 'SUBESTACION',
  /** Parques eólicos - MIEA:cEolicas */
  WIND_FARM = 'PARQUE_EOLICO',
  /** Plantas fotovoltaicas/solares - MIEA:csolares */
  SOLAR_PLANT = 'PLANTA_SOLAR',
  /** Centrales térmicas - MIEA:ctermicas */
  THERMAL_PLANT = 'CENTRAL_TERMICA',
  /** Plantas de biomasa - MIEA:cBiomasa */
  BIOMASS_PLANT = 'PLANTA_BIOMASA',
  /** Centrales hidroeléctricas - MIEA:cHidroelectricas */
  HYDROELECTRIC = 'HIDROELECTRICA',
  /** Plantas de cogeneración - MIEA:cCogeneracion */
  COGENERATION = 'COGENERACION',
  /** Gasoductos - MIEA:cGasoductos */
  GAS_PIPELINE = 'GASODUCTO',
  /** Estaciones de compresión de gas - MIEA:cCompresion */
  GAS_COMPRESSION = 'ESTACION_COMPRESION',
  /** Líneas eléctricas - MIEA:LineasElect */
  POWER_LINE = 'LINEA_ELECTRICA',
  /** Almacenamiento petrolífero - MIEA:cAlmacenamiento */
  OIL_STORAGE = 'ALMACENAMIENTO_PETROLEO',
  /** Refinerías - MIEA:cRefinerias */
  REFINERY = 'REFINERIA',
  /** Oleoductos - MIEA:coleoductos */
  OIL_PIPELINE = 'OLEODUCTO',
  /** Biogás - MIEA:cBiogas_otros */
  BIOGAS = 'BIOGAS',
  /** Regasificación - MIEA:cRegasificacion */
  REGASIFICATION = 'REGASIFICACION'
}

/**
 * Mapeo de tipos de infraestructura a capas WFS reales
 * Basado en GetCapabilities del WFS AAE (namespace MIEA)
 */
export const ENERGY_LAYER_MAP: Record<EnergyFacilityType, string> = {
  [EnergyFacilityType.SUBSTATION]: 'MIEA:subestaciones',
  [EnergyFacilityType.WIND_FARM]: 'MIEA:cEolicas',
  [EnergyFacilityType.SOLAR_PLANT]: 'MIEA:csolares',
  [EnergyFacilityType.THERMAL_PLANT]: 'MIEA:ctermicas',
  [EnergyFacilityType.BIOMASS_PLANT]: 'MIEA:cBiomasa',
  [EnergyFacilityType.HYDROELECTRIC]: 'MIEA:cHidroelectricas',
  [EnergyFacilityType.COGENERATION]: 'MIEA:cCogeneracion',
  [EnergyFacilityType.GAS_PIPELINE]: 'MIEA:cGasoductos',
  [EnergyFacilityType.GAS_COMPRESSION]: 'MIEA:cCompresion',
  [EnergyFacilityType.POWER_LINE]: 'MIEA:LineasElect',
  [EnergyFacilityType.OIL_STORAGE]: 'MIEA:cAlmacenamiento',
  [EnergyFacilityType.REFINERY]: 'MIEA:cRefinerias',
  [EnergyFacilityType.OIL_PIPELINE]: 'MIEA:coleoductos',
  [EnergyFacilityType.BIOGAS]: 'MIEA:cBiogas_otros',
  [EnergyFacilityType.REGASIFICATION]: 'MIEA:cRegasificacion'
};

/**
 * Capas principales para búsqueda por defecto
 * Ordenadas por relevancia para PTEL (infraestructuras críticas)
 */
export const PRIMARY_ENERGY_LAYERS: string[] = [
  'MIEA:subestaciones',      // Subestaciones eléctricas (críticas)
  'MIEA:csolares',           // Plantas fotovoltaicas
  'MIEA:cEolicas',           // Parques eólicos
  'MIEA:ctermicas',          // Centrales térmicas
  'MIEA:cHidroelectricas',   // Hidroeléctricas
  'MIEA:cBiomasa',           // Biomasa
  'MIEA:cCogeneracion'       // Cogeneración
];

/**
 * Opciones de búsqueda específicas para infraestructuras energéticas
 */
export interface EnergySearchOptions extends WFSSearchOptions {
  /** Tipo de infraestructura energética */
  facilityType?: EnergyFacilityType;
  /** Código INE del municipio para filtro BBOX */
  codigoINE?: string;
  /** Nivel de tensión mínimo (kV) para subestaciones */
  minVoltage?: number;
  /** Potencia mínima instalada (MW) */
  minPower?: number;
}

/**
 * Geocodificador especializado para infraestructuras energéticas andaluzas
 * 
 * IMPORTANTE: El WFS de AAE no incluye campos MUNICIPIO/PROVINCIA,
 * por lo que la búsqueda por municipio requiere:
 * 1. Obtener BBOX del municipio vía MunicipalBoundaryService
 * 2. Filtrar features espacialmente con BBOX
 * 3. Buscar por nombre con fuzzy matching
 * 
 * Precisión esperada: ±10-20m (coordenadas oficiales AAE)
 * Cobertura: ~2,000 infraestructuras energéticas
 * 
 * @example
 * ```typescript
 * const geocoder = new WFSEnergyGeocoder();
 * 
 * // Búsqueda con código INE (recomendado)
 * const result = await geocoder.geocodeByMunicipality({
 *   name: 'Subestación Peligros',
 *   codigoINE: '18153'  // Peligros, Granada
 * });
 * 
 * // Búsqueda con BBOX directo
 * const result2 = await geocoder.geocode({
 *   name: 'Parque Eólico',
 *   bbox: [430000, 4080000, 435000, 4085000]
 * });
 * ```
 * 
 * @version 2.0.0 - F2.T4: Integración con MunicipalBoundaryService para BBOX
 */
export class WFSEnergyGeocoder extends WFSBaseGeocoder {
  
  /** Cache de features por capa+BBOX */
  private layerCache: Map<string, WFSFeature[]>;

  constructor(config?: Partial<SpecializedGeocoderConfig>) {
    super(config);
    this.layerCache = new Map();
  }

  /**
   * Configuración específica para WFS Agencia Andaluza Energía
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      wfsEndpoint: 'https://www.agenciaandaluzadelaenergia.es/mapwms/wfs',
      layerName: 'MIEA:subestaciones', // Capa por defecto
      fuzzyThreshold: 0.35, // Threshold permisivo (nombres técnicos variables)
      timeout: 20000, // 20s (servicio a veces lento)
      outputSRS: 'EPSG:25830'
    };
  }

  /**
   * Obtiene BBOX del municipio por código INE
   * Utiliza MunicipalBoundaryService integrado en F2.T2
   * 
   * @param codigoINE - Código INE del municipio (5 dígitos)
   * @returns BBOX en EPSG:25830 [minX, minY, maxX, maxY] o null
   */
  public async getBBoxByINEValue(codigoINE: string): Promise<[number, number, number, number] | null> {
    const bbox = await getBBoxByINE(codigoINE);
    if (!bbox) {
      console.warn(`[WFSEnergyGeocoder] No se encontró BBOX para INE ${codigoINE}`);
      return null;
    }
    // BBox ya es [minX, minY, maxX, maxY]
    return bbox;
  }

  /**
   * Obtiene BBOX del municipio por nombre
   * 
   * @param municipio - Nombre del municipio
   * @returns BBOX en EPSG:25830 o null
   */
  public async getBBoxByNameValue(
    municipio: string
  ): Promise<[number, number, number, number] | null> {
    const bbox = await getBBoxByName(municipio);
    if (!bbox) {
      console.warn(`[WFSEnergyGeocoder] No se encontró BBOX para ${municipio}`);
      return null;
    }
    // BBox ya es [minX, minY, maxX, maxY]
    return bbox;
  }

  /**
   * Parsea feature GeoJSON de AAE
   * 
   * Estructura típica de subestaciones:
   * {
   *   type: "Feature",
   *   geometry: { type: "Point", coordinates: [x, y] },
   *   properties: {
   *     NOMBRE: "SET Peligros 220/66 kV",
   *     TENSION_MAX: "220 kV",
   *     NIVELES_TENSION_kV: "220/66",
   *     PROPIETARIO: "Red Eléctrica"
   *   }
   * }
   * 
   * Estructura de parques eólicos/solares:
   * {
   *   properties: {
   *     NOMBRE: "P.E. Los Montes",
   *     POTENCIA: "50 MW",
   *     TECNOLOGIA: "Fotovoltaica" (solo csolares)
   *   }
   * }
   * 
   * NOTA: No hay campos MUNICIPIO/PROVINCIA - se obtienen del BBOX
   */
  protected parseFeature(feature: GeoJSONFeature): WFSFeature | null {
    try {
      const props = feature.properties || {};
      const geom = feature.geometry;

      // Validar geometría
      if (!geom || !geom.coordinates) {
        console.warn('[WFSEnergyGeocoder] Feature sin geometría válida:', feature.id);
        return null;
      }

      // Manejar diferentes tipos de geometría
      let x: number, y: number;
      
      if (geom.type === 'Point' || geom.type === 'MultiPoint') {
        // Punto / MultiPoint(N=1) vía primitiva centralizada (sub-0a WMPF)
        const pt = this.parseGeometry(geom, `Energy feature ${String(feature.id ?? 'sin-id')}`);
        if (!pt) return null;
        [x, y] = pt;
      } else if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
        // Calcular centroide aproximado
        const allCoords = geom.coordinates as number[][][];
        const coords = geom.type === 'Polygon' 
          ? allCoords[0] 
          : (allCoords[0] as unknown as number[][][])[0];
        
        if (!coords || coords.length === 0) return null;
        
        const sumX = coords.reduce((sum: number, c: number[]) => sum + c[0], 0);
        const sumY = coords.reduce((sum: number, c: number[]) => sum + c[1], 0);
        x = sumX / coords.length;
        y = sumY / coords.length;
      } else if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
        // Líneas eléctricas (`MIEA:LineasElect`) y gasoductos
        // (`MIEA:cGasoductos`). Delegan en `parseGeometry` que ahora
        // soporta MultiLineString(N≥1) vía midpoint linear-referenced
        // sobre la unión de partes (DOP-WHLG-2=B post-sub-WEMP).
        const pt = this.parseGeometry(geom, `Energy feature ${String(feature.id ?? 'sin-id')}`);
        if (!pt) return null;
        [x, y] = pt;
      } else {
        console.warn('[WFSEnergyGeocoder] Tipo de geometría no soportado:', geom.type);
        return null;
      }

      // Validar coordenadas UTM30 Andalucía
      if (x < 100000 || x > 800000 || y < 3900000 || y > 4350000) {
        console.warn('[WFSEnergyGeocoder] Coordenadas fuera de rango UTM30:', x, y);
        return null;
      }

      // Extraer nombre (campo común a todas las capas)
      const name = getStringProp(props, 'NOMBRE', 'nombre');
      
      if (!name) {
        console.warn('[WFSEnergyGeocoder] Feature sin nombre');
        return null;
      }

      return {
        name,
        x,
        y,
        municipality: '', // No disponible en WFS AAE
        province: '', // No disponible en WFS AAE
        address: '',
        properties: {
          // Campos de subestaciones
          tensionMax: props.TENSION_MAX,
          nivelesTension: props.NIVELES_TENSION_kV,
          // Campos de parques eólicos/solares
          potencia: props.POTENCIA,
          tecnologia: props.TECNOLOGIA,
          // Campo común
          propietario: props.PROPIETARIO,
          // Preservar todos los campos originales
          ...props
        }
      };
    } catch (error) {
      console.error('[WFSEnergyGeocoder] Error parseando feature:', error);
      return null;
    }
  }

  /**
   * Construye filtro CQL específico para infraestructuras energéticas
   * NOTA: No usa filtros MUNICIPIO/PROVINCIA (no existen en AAE)
   * Solo usa filtros espaciales BBOX
   */
  protected buildCQLFilter(options: EnergySearchOptions): string {
    const filters: string[] = [];

    // BBOX es el filtro principal (no hay campos municipio/provincia)
    // El filtro BBOX se añade en buildWFSParams, no aquí

    // Filtro por nombre si se proporciona (búsqueda aproximada)
    if (options.name && options.name.length > 2) {
      const escapedName = this.escapeCQL(options.name);
      filters.push(`NOMBRE ILIKE '%${escapedName}%'`);
    }

    return filters.length > 0 ? filters.join(' AND ') : '';
  }

  /**
   * Detecta tipo de infraestructura energética por nombre
   * IMPORTANTE: El orden de los checks importa - más específico primero
   */
  public detectFacilityType(name: string): EnergyFacilityType {
    const nameLower = name.toLowerCase();
    
    // Líneas eléctricas (ANTES de subestaciones porque "LAT 400 kV" incluye "kv")
    if (nameLower.includes('línea') || nameLower.includes('linea') ||
        nameLower.includes('l.a.t.') || nameLower.startsWith('lat ')) {
      return EnergyFacilityType.POWER_LINE;
    }
    
    // Subestaciones
    if (nameLower.includes('subestación') || nameLower.includes('subestacion') ||
        nameLower.includes('set ') || nameLower.includes('s.e.t.') ||
        nameLower.match(/\d+\s*kv/)) {
      return EnergyFacilityType.SUBSTATION;
    }
    
    // Parques eólicos
    if (nameLower.includes('eólico') || nameLower.includes('eolico') ||
        nameLower.includes('aerogenerador') || nameLower.startsWith('p.e.') ||
        nameLower.startsWith('pe ') || nameLower.includes('parque eólico')) {
      return EnergyFacilityType.WIND_FARM;
    }
    
    // Plantas solares/fotovoltaicas
    if (nameLower.includes('fotovoltaica') || nameLower.includes('solar') ||
        nameLower.includes('huerto solar') || nameLower.includes('planta solar') ||
        nameLower.startsWith('pf ') || nameLower.includes('termosolar')) {
      return EnergyFacilityType.SOLAR_PLANT;
    }
    
    // Centrales térmicas
    if (nameLower.includes('térmica') || nameLower.includes('termica') ||
        nameLower.includes('ciclo combinado') || nameLower.includes('central térmica')) {
      return EnergyFacilityType.THERMAL_PLANT;
    }
    
    // Biomasa
    if (nameLower.includes('biomasa')) {
      return EnergyFacilityType.BIOMASS_PLANT;
    }
    
    // Hidroeléctricas
    if (nameLower.includes('hidroeléctrica') || nameLower.includes('hidroelectrica') ||
        nameLower.includes('central hidroeléctrica') || nameLower.includes('salto')) {
      return EnergyFacilityType.HYDROELECTRIC;
    }
    
    // Cogeneración
    if (nameLower.includes('cogeneración') || nameLower.includes('cogeneracion')) {
      return EnergyFacilityType.COGENERATION;
    }
    
    // Gas
    if (nameLower.includes('gasoducto')) {
      return EnergyFacilityType.GAS_PIPELINE;
    }
    if (nameLower.includes('compresión') || nameLower.includes('compresion') ||
        nameLower.includes('estación de gas')) {
      return EnergyFacilityType.GAS_COMPRESSION;
    }
    if (nameLower.includes('regasificación') || nameLower.includes('regasificacion')) {
      return EnergyFacilityType.REGASIFICATION;
    }
    
    // Petróleo
    if (nameLower.includes('refinería') || nameLower.includes('refineria')) {
      return EnergyFacilityType.REFINERY;
    }
    if (nameLower.includes('oleoducto')) {
      return EnergyFacilityType.OIL_PIPELINE;
    }
    if (nameLower.includes('almacenamiento') && 
        (nameLower.includes('petróleo') || nameLower.includes('petroleo') || 
         nameLower.includes('combustible'))) {
      return EnergyFacilityType.OIL_STORAGE;
    }
    
    // Biogás
    if (nameLower.includes('biogás') || nameLower.includes('biogas')) {
      return EnergyFacilityType.BIOGAS;
    }
    
    // Default: subestación (más común en PTEL)
    return EnergyFacilityType.SUBSTATION;
  }

  /**
   * Obtiene la capa WFS correspondiente a un tipo de infraestructura
   */
  public getLayerForType(type: EnergyFacilityType): string {
    return ENERGY_LAYER_MAP[type];
  }

  /**
   * Geocodifica con código INE del municipio
   * Método principal recomendado para búsquedas por municipio
   * 
   * @param options - Opciones de búsqueda con codigoINE obligatorio
   * @returns Resultado de geocodificación o null
   */
  public async geocodeByMunicipality(options: EnergySearchOptions): Promise<GeocodingResult | null> {
    const { name, codigoINE, municipality, province: _province, facilityType } = options;
    
    // Obtener BBOX del municipio
    let bbox: [number, number, number, number] | null = null;
    
    if (codigoINE) {
      bbox = await this.getBBoxByINEValue(codigoINE);
    } else if (municipality) {
      bbox = await this.getBBoxByNameValue(municipality);
    }
    
    if (!bbox && !options.bbox) {
      console.warn('[WFSEnergyGeocoder] No se pudo obtener BBOX para búsqueda espacial');
      // Intentar búsqueda sin BBOX (menos precisa, puede devolver resultados de toda Andalucía)
    }

    // Usar BBOX obtenido o el proporcionado
    const searchBbox = options.bbox || bbox;
    
    // Detectar tipo de instalación si no se especifica
    const detectedType = facilityType || this.detectFacilityType(name);
    const targetLayer = ENERGY_LAYER_MAP[detectedType];
    
    // Intentar con la capa detectada
    let result = await this.searchInLayer(targetLayer, name, searchBbox);
    
    if (result && result.confidence >= 70) {
      return result;
    }
    
    // Si no encuentra, probar otras capas principales
    for (const layer of PRIMARY_ENERGY_LAYERS) {
      if (layer === targetLayer) continue;
      
      result = await this.searchInLayer(layer, name, searchBbox);
      
      if (result && result.confidence >= 70) {
        return result;
      }
    }
    
    return result;
  }

  /**
   * Geocodifica con autodetección de capa basada en el nombre
   * Método requerido por GeocodingOrchestrator
   * 
   * @param options - Opciones de búsqueda
   * @returns Resultado de geocodificación o null
   */
  public async geocodeWithAutoLayer(options: EnergySearchOptions): Promise<GeocodingResult | null> {
    // Detectar tipo de instalación por nombre
    const detectedType = options.facilityType || this.detectFacilityType(options.name);
    const targetLayer = ENERGY_LAYER_MAP[detectedType];
    
    // Obtener BBOX del municipio si hay código INE o nombre
    let bbox: [number, number, number, number] | null = null;
    
    if (options.codigoINE) {
      bbox = await this.getBBoxByINEValue(options.codigoINE);
    } else if (options.municipality) {
      bbox = await this.getBBoxByNameValue(options.municipality);
    }
    
    const searchBbox = options.bbox || bbox;
    
    // Intentar con la capa detectada primero
    let result = await this.searchInLayer(targetLayer, options.name, searchBbox);
    
    if (result && result.confidence >= 70) {
      return result;
    }
    
    // Si no encuentra, probar otras capas principales
    for (const layer of PRIMARY_ENERGY_LAYERS) {
      if (layer === targetLayer) continue;
      
      result = await this.searchInLayer(layer, options.name, searchBbox);
      
      if (result && result.confidence >= 70) {
        return result;
      }
    }
    
    return result;
  }

  /**
   * Busca en una capa WFS específica
   */
  private async searchInLayer(
    layer: string,
    searchName: string,
    bbox?: [number, number, number, number] | null
  ): Promise<GeocodingResult | null> {
    try {
      // Construir parámetros WFS
      const params: Record<string, string> = {
        service: 'WFS',
        version: '2.0.0',
        request: 'GetFeature',
        typeName: layer,
        outputFormat: 'application/json',
        srsName: 'EPSG:25830',
        count: '100' // Limitar resultados
      };
      
      // Añadir filtro BBOX si está disponible
      if (bbox) {
        const [minX, minY, maxX, maxY] = bbox;
        params['BBOX'] = `${minY},${minX},${maxY},${maxX},EPSG:25830`;
      }
      
      // Ejecutar petición
      const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
      
      // Parsear features
      const features = this.parseResponse(response.data);
      
      if (features.length === 0) {
        return null;
      }
      
      // Buscar mejor match por nombre (usando método privado, no el de la clase base)
      const bestMatch = this.findBestMatchLocal(searchName, features);
      
      if (!bestMatch) {
        return null;
      }
      
      // Construir resultado
      return {
        x: bestMatch.feature.x,
        y: bestMatch.feature.y,
        confidence: bestMatch.score,
        source: `WFSEnergy:${layer}`,
        matchedName: bestMatch.feature.name,
        metadata: {
          layer,
          ...bestMatch.feature.properties
        }
      };
      
    } catch (error) {
      console.error(`[WFSEnergyGeocoder] Error buscando en ${layer}:`, error);
      return null;
    }
  }

  /**
   * Parsea respuesta GeoJSON del WFS
   */
  protected parseResponse(data: WFSResponse): WFSFeature[] {
    const features: WFSFeature[] = [];
    
    try {
      const geoJson = data as GeoJSONFeatureCollection;
      const geoJsonFeatures = geoJson?.features || [];
      
      for (const feature of geoJsonFeatures) {
        const parsed = this.parseFeature(feature);
        if (parsed) {
          features.push(parsed);
        }
      }
    } catch (error) {
      console.error('[WFSEnergyGeocoder] Error parseando respuesta:', error);
    }
    
    return features;
  }

  /**
   * Encuentra mejor match por nombre usando fuzzy matching
   * Método privado que no sobreescribe el de la clase base
   */
  private findBestMatchLocal(
    searchName: string,
    features: WFSFeature[]
  ): { feature: WFSFeature; score: number } | null {
    if (features.length === 0) return null;
    
    const searchLower = searchName.toLowerCase().trim();
    let bestMatch: { feature: WFSFeature; score: number } | null = null;
    
    for (const feature of features) {
      const featureName = feature.name.toLowerCase().trim();
      
      // Match exacto
      if (featureName === searchLower) {
        return { feature, score: 100 };
      }
      
      // Contiene el texto buscado
      if (featureName.includes(searchLower) || searchLower.includes(featureName)) {
        const score = 85;
        if (!bestMatch || score > bestMatch.score) {
          bestMatch = { feature, score };
        }
        continue;
      }
      
      // Similitud de palabras
      const searchWords = searchLower.split(/\s+/);
      const featureWords = featureName.split(/\s+/);
      
      let matchedWords = 0;
      for (const sw of searchWords) {
        if (sw.length < 3) continue;
        for (const fw of featureWords) {
          if (fw.includes(sw) || sw.includes(fw)) {
            matchedWords++;
            break;
          }
        }
      }
      
      if (matchedWords > 0) {
        const score = Math.min(75, 50 + matchedWords * 10);
        if (!bestMatch || score > bestMatch.score) {
          bestMatch = { feature, score };
        }
      }
    }
    
    return bestMatch;
  }

  /**
   * Obtiene todas las infraestructuras energéticas de un municipio
   * 
   * @param codigoINE - Código INE del municipio
   * @returns Array de features encontradas
   */
  public async getAllFacilitiesInMunicipality(codigoINE: string): Promise<WFSFeature[]> {
    const bbox = await this.getBBoxByINEValue(codigoINE);
    
    if (!bbox) {
      console.warn(`[WFSEnergyGeocoder] No se encontró BBOX para INE ${codigoINE}`);
      return [];
    }
    
    const allFeatures: WFSFeature[] = [];
    
    for (const layer of PRIMARY_ENERGY_LAYERS) {
      try {
        const params: Record<string, string> = {
          service: 'WFS',
          version: '2.0.0',
          request: 'GetFeature',
          typeName: layer,
          outputFormat: 'application/json',
          srsName: 'EPSG:25830',
          count: '500',
          BBOX: `${bbox[1]},${bbox[0]},${bbox[3]},${bbox[2]},EPSG:25830`
        };
        
        const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
        const features = this.parseResponse(response.data);
        
        // Añadir información de la capa
        for (const f of features) {
          f.properties._layer = layer;
          f.properties._type = this.getTypeFromLayer(layer);
        }
        
        allFeatures.push(...features);
        
      } catch (error) {
        console.warn(`[WFSEnergyGeocoder] Error obteniendo ${layer}:`, error);
      }
    }
    
    return allFeatures;
  }

  /**
   * Obtiene el tipo de infraestructura desde el nombre de la capa
   */
  private getTypeFromLayer(layer: string): string {
    for (const [type, layerName] of Object.entries(ENERGY_LAYER_MAP)) {
      if (layerName === layer) return type;
    }
    return 'DESCONOCIDO';
  }

  /**
   * Busca subestaciones por nivel de tensión en un municipio
   * 
   * @param codigoINE - Código INE del municipio
   * @param minVoltage - Tensión mínima en kV (ej: 66, 132, 220, 400)
   */
  public async findSubstationsByVoltage(
    codigoINE: string,
    minVoltage: number
  ): Promise<WFSFeature[]> {
    const bbox = await this.getBBoxByINEValue(codigoINE);
    
    if (!bbox) return [];
    
    try {
      const params: Record<string, string> = {
        service: 'WFS',
        version: '2.0.0',
        request: 'GetFeature',
        typeName: 'MIEA:subestaciones',
        outputFormat: 'application/json',
        srsName: 'EPSG:25830',
        count: '100',
        BBOX: `${bbox[1]},${bbox[0]},${bbox[3]},${bbox[2]},EPSG:25830`
      };
      
      const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
      const features = this.parseResponse(response.data);
      
      // Filtrar por tensión (campo TENSION_MAX es string como "220 kV")
      return features.filter(f => {
        const tensionStr = String(f.properties.tensionMax || f.properties.TENSION_MAX || '');
        const tensionMatch = tensionStr.match(/(\d+)/);
        if (tensionMatch) {
          const tension = parseInt(tensionMatch[1], 10);
          return tension >= minVoltage;
        }
        return false;
      });
      
    } catch (error) {
      console.error('[WFSEnergyGeocoder] Error buscando subestaciones:', error);
      return [];
    }
  }

  /**
   * Valida si un punto está dentro del BBOX de un municipio
   * Utiliza función validatePointInMunicipality del servicio de límites
   */
  public async validatePointInMunicipalityBBox(
    x: number,
    y: number,
    codigoINE: string
  ): Promise<boolean> {
    const validation = await validatePointInMunicipality(
      x, y, codigoINE, 'EPSG:25830'
    );
    return validation?.isValid ?? false;
  }
}

export default WFSEnergyGeocoder;
